import { useApolloClient, useQuery } from "@apollo/client";
import { Show, useAuth, useOrganizationList } from "@clerk/react";
import {
  PrimaryFullScreenSpinner,
  StaleVersionProvider,
  StaleVersionRibbon,
  TooltipProvider,
} from "@repo/ui";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Suspense, useEffect, useRef } from "react";
import { HotkeysProvider } from "react-hotkeys-hook";
import Nav from "~/components/nav/nav";
import { RedirectToSignInWithDelay } from "~/components/RedirectToSignInWithDelay";
import { config } from "~/config";
import { graphql } from "~/gql/client";

export const Route = createFileRoute("/__auth")({
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function SignedInLayout() {
  const auth = useAuth();
  const client = useApolloClient();
  const { isLoaded: orgsIsLoaded, setActive } = useOrganizationList();
  const previousOrg = useRef(auth.orgId);
  const currentOrg = auth.orgId;

  // switch the user to primary ops if its not the active org
  useEffect(() => {
    if (
      auth.isLoaded &&
      auth.isSignedIn &&
      !auth.sessionClaims.primary_ops &&
      orgsIsLoaded
    ) {
      void setActive({ organization: config.primaryOpsOrgId }).catch(() => {
        void auth.signOut();
      });
    }
  }, [
    auth.isLoaded,
    auth.isSignedIn,
    auth.sessionClaims?.primary_ops,
    auth.signOut,
    orgsIsLoaded,
    setActive,
  ]);

  useEffect(() => {
    if (previousOrg.current && previousOrg.current !== currentOrg) {
      previousOrg.current = currentOrg;
      if (currentOrg === config.primaryOpsOrgId) {
        client.resetStore();
      } else {
        client.clearStore();
      }
    }
  }, [currentOrg, client]);

  const { data } = useQuery(
    graphql(`
      query OpsShell {
        ... @defer {
          appEnvironment {
            id
            environment
            appBaseUrl
            commitSha
          }
        }
        ... @defer {
          organisations {
            id
            name
          }
        }
        ... @defer {
          transactionQueue {
            complianceReview {
              id
              count
            }
            readyForProcessingToday {
              id
              count
            }
            readyForProcessingLater {
              id
              count
            }
            inProgress {
              id
              count
            }
            upcoming {
              id
              count
            }
          }
        }
        ... @defer {
          transactionMonitoringFindings(first: 1, filter: {statuses: [OPEN]}) {
            totalCount
          }
        }
        ... @defer {
          accountOnboardingRequestsQueue {
            id
            pendingCount
          }
        }
        ... @defer {
          stripePayoutQueueItems(first: 100, filter: {statuses: [PENDING, RECEIVED]}) {
            stripePayoutQueueItems {
              id
            }
          }
        }
        ... @defer {
          pendingBeneficiaryAccountsCount
        }
        ... @defer {
          calastoneTransactions(first: 20, filter: {statuses: [TO_PROCESS]}) {
            calastoneTransactions {
              id
            }
          }
        }
        ... @defer {
          uncategorisedTransactionsCount
        }
        ... @defer {
          businessEntitiesWithoutKycUri {
            id
          }
        }
        ... @defer {
          fxTrades {
            status
          }
        }
      }
    `),
  );

  return (
    <StaleVersionProvider
      currentVersion={import.meta.env.VITE_OPS_PORTAL_VERSION}
    >
      <HotkeysProvider initiallyActiveScopes={["command-bar"]}>
        <TooltipProvider delayDuration={100}>
          <div className="grid size-full grid-cols-[auto_1fr] text-primary">
            <Nav
              transactionQueue={data?.transactionQueue}
              transactionMonitoringOpenFindingsCount={
                data?.transactionMonitoringFindings?.totalCount
              }
              onboardingRequestsCount={
                data?.accountOnboardingRequestsQueue?.pendingCount
              }
              stripePayoutQueueCount={
                data?.stripePayoutQueueItems === null
                  ? null
                  : data?.stripePayoutQueueItems?.stripePayoutQueueItems.length
              }
              pendingBeneficiaryAccountsCount={
                data?.pendingBeneficiaryAccountsCount
              }
              calatsonePricingQueueCount={
                data?.calastoneTransactions === null
                  ? null
                  : (data?.calastoneTransactions?.calastoneTransactions
                      ?.length ?? null)
              }
              unreconciledTransactionsCount={
                data?.uncategorisedTransactionsCount
              }
              kycQueueCount={
                data?.businessEntitiesWithoutKycUri === null
                  ? null
                  : (data?.businessEntitiesWithoutKycUri?.length ?? null)
              }
              fxTradesRequiresInstructionCount={
                data?.fxTrades === null
                  ? null
                  : data?.fxTrades?.filter(
                      (fxTrade) => fxTrade.status === "REQUIRES_INSTRUCTION",
                    ).length
              }
              commandBarSuggestions={{
                organisations: data?.organisations ?? undefined,
              }}
              appEnvironment={data?.appEnvironment?.environment ?? undefined}
              version={data?.appEnvironment?.commitSha ?? undefined}
              switchRoute={
                data?.appEnvironment?.appBaseUrl
                  ? `${data.appEnvironment.appBaseUrl}/switch-organisation`
                  : undefined
              }
            />
            <div
              className="flex-grow overflow-y-auto rounded-xl rounded-tr-none rounded-br-none border-t border-r-0 border-b border-l bg-canvas shadow-overlay-left"
              style={{
                scrollbarGutter: "stable",
              }}
            >
              <StaleVersionRibbon />
              <div className="flex w-full items-center justify-center">
                <div className="relative w-full p-4">
                  <div className="min-h-screen">
                    <Outlet />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TooltipProvider>
      </HotkeysProvider>
    </StaleVersionProvider>
  );
}

function View() {
  return (
    <>
      <Show when="signed-in">
        <SignedInLayout />
      </Show>
      <Show when="signed-out">
        <PrimaryFullScreenSpinner />
        <RedirectToSignInWithDelay />
      </Show>
    </>
  );
}
